/**
 * A comparação plano a plano, recurso a recurso.
 *
 * **As linhas saem de `featureKeys`, do servidor, na ordem que ele mandou.** A
 * tela só sabe TRADUZIR chave em rótulo (`rotuloDaChave`); a lista não mora
 * aqui. Se a lista morasse na tela, um recurso novo no banco não apareceria
 * para ninguém, e um recurso removido continuaria sendo prometido — que é a
 * versão "tabela de preços" de anunciar alarme sem detector.
 *
 * Duas leituras, os MESMOS dados:
 *  - **desktop (≥768px):** `<table>` de verdade, com `<th scope>` nas duas
 *    direções e a coluna de recurso grudada à esquerda no scroll;
 *  - **celular:** um bloco por plano com a lista recurso→valor. Uma tabela de
 *    6 colunas em 390px ou estoura a largura ou comprime até ninguém ler, e as
 *    duas saídas são piores do que empilhar.
 */
import { Badge, cn, NO_DATA_DASH } from '@mgdlead/ui';

import {
  descreverRecurso,
  NAO_INCLUSO,
  precoDoCiclo,
  rotuloDaChave,
  totalFormatado,
} from './billing-ui';
import type { BillingInterval, CatalogPlan } from '../../lib/api';

export interface TabelaComparativaProps {
  plans: readonly CatalogPlan[];
  featureKeys: readonly string[];
  interval: BillingInterval;
  /** Código do plano que o workspace já usa, quando há um. */
  planoAtual?: string | null;
  className?: string;
}

function Valor({ plan, chave }: { plan: CatalogPlan; chave: string }) {
  const valor = descreverRecurso(plan.features[chave]);

  // Sem dado ≠ zero: travessão, e o leitor de tela ouve o porquê.
  if (valor === null) {
    return (
      <span className="nums text-muted-foreground">
        <span aria-hidden="true">{NO_DATA_DASH}</span>
        <span className="sr-only">sem dado</span>
      </span>
    );
  }

  if (valor === NAO_INCLUSO) {
    return <span className="text-muted-foreground">{valor}</span>;
  }

  return <span className="nums font-medium">{valor}</span>;
}

export function TabelaComparativa({
  plans,
  featureKeys,
  interval,
  planoAtual = null,
  className,
}: TabelaComparativaProps) {
  if (plans.length === 0 || featureKeys.length === 0) return null;

  return (
    <section className={cn('flex flex-col gap-3', className)} aria-labelledby="comparativo-titulo">
      <h2 id="comparativo-titulo" className="text-lg font-semibold tracking-tight">
        Compare os planos
      </h2>

      {/* ------------------------------ desktop ------------------------------ */}
      <div className="max-md:hidden overflow-x-auto rounded-lg border border-border bg-surface">
        <table className="w-full border-collapse text-sm">
          <caption className="sr-only">
            Comparação dos planos por recurso. A primeira coluna traz o recurso; as demais, o
            valor em cada plano.
          </caption>
          <thead>
            <tr className="border-b-2 border-border-strong">
              <th
                scope="col"
                className="sticky left-0 z-10 bg-surface px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground"
              >
                Recurso
              </th>
              {plans.map((plan) => {
                const preco = precoDoCiclo(plan, interval);
                return (
                  <th key={plan.code} scope="col" className="px-3 py-2 text-left align-bottom">
                    <span className="flex flex-col gap-0.5">
                      <span className="flex items-center gap-1.5">
                        <span className="text-sm font-semibold text-foreground">{plan.name}</span>
                        {plan.code === planoAtual ? (
                          <Badge tone="info" dot>
                            Seu plano
                          </Badge>
                        ) : null}
                      </span>
                      <span className="nums text-xs font-normal text-muted-foreground">
                        {preco === null ? NO_DATA_DASH : totalFormatado(preco)}
                      </span>
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {featureKeys.map((chave, indice) => (
              <tr
                key={chave}
                className={cn('h-9 border-b border-border last:border-b-0', indice % 2 === 1 && 'bg-muted/40')}
              >
                <th
                  scope="row"
                  className={cn(
                    'sticky left-0 z-10 px-3 py-2 text-left text-sm font-normal text-muted-foreground',
                    indice % 2 === 1 ? 'bg-muted' : 'bg-surface',
                  )}
                >
                  {rotuloDaChave(chave)}
                </th>
                {plans.map((plan) => (
                  <td key={plan.code} className="px-3 py-2">
                    <Valor plan={plan} chave={chave} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ------------------------------ celular ------------------------------ */}
      <div className="flex flex-col gap-3 md:hidden">
        {plans.map((plan) => {
          const preco = precoDoCiclo(plan, interval);
          return (
            <div key={plan.code} className="rounded-lg border border-border bg-surface">
              <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-border px-3 py-2">
                <span className="flex items-center gap-1.5">
                  <span className="text-sm font-semibold">{plan.name}</span>
                  {plan.code === planoAtual ? (
                    <Badge tone="info" dot>
                      Seu plano
                    </Badge>
                  ) : null}
                </span>
                <span className="nums text-sm text-muted-foreground">
                  {preco === null ? NO_DATA_DASH : totalFormatado(preco)}
                </span>
              </div>

              <dl className="flex flex-col">
                {featureKeys.map((chave, indice) => (
                  <div
                    key={chave}
                    className={cn(
                      'flex min-h-11 items-center justify-between gap-3 px-3 py-2',
                      indice % 2 === 1 && 'bg-muted/40',
                    )}
                  >
                    <dt className="min-w-0 text-sm text-muted-foreground">{rotuloDaChave(chave)}</dt>
                    <dd className="shrink-0 text-right text-sm">
                      <Valor plan={plan} chave={chave} />
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          );
        })}
      </div>
    </section>
  );
}
